import { Tooltip } from '@design/components/tooltip/Tooltip';
import { MouseEvent, ReactNode, RefObject, useState } from 'react';
import { createPortal } from 'react-dom';
import { isInsightMentionType, Mention } from '../types';
import {
  getChipLabelElement,
  isMentionChipElement,
  readMentionFromChipElement,
} from './dom-chip';
import styles from './MentionComposerInput.module.scss';

export interface UseResolvedMentionPreviewArgs {
  editorRef: RefObject<HTMLDivElement | null>;
  /** Renders a hover preview for a resolved chip. Return `null` to show none. */
  renderPreview?: (mention: Mention) => ReactNode | null;
}

export interface UseResolvedMentionPreviewResult {
  onChipMouseOver: (e: MouseEvent<HTMLDivElement>) => void;
  onChipMouseOut: (e: MouseEvent<HTMLDivElement>) => void;
  clearHoveredChip: () => void;
  chipPreviewTooltip: ReactNode;
}

const findMentionChipFromTarget = (
  root: HTMLElement,
  target: EventTarget | null
): HTMLElement | null => {
  if (!(target instanceof Node)) {
    return null;
  }
  const el =
    target instanceof Element
      ? target.closest('[data-mention-chip]')
      : target.parentElement?.closest('[data-mention-chip]');
  if (!isMentionChipElement(el) || !root.contains(el)) {
    return null;
  }
  return el;
};

export const useResolvedMentionPreview = ({
  editorRef,
  renderPreview,
}: UseResolvedMentionPreviewArgs): UseResolvedMentionPreviewResult => {
  const [hoveredChip, setHoveredChip] = useState<HTMLElement | null>(null);
  const [hoveredChipRect, setHoveredChipRect] = useState<DOMRect | null>(null);

  const onChipMouseOver = (e: MouseEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root) {
      return;
    }
    const el = findMentionChipFromTarget(root, e.target);
    if (el && el !== hoveredChip) {
      setHoveredChip(el);
      setHoveredChipRect(el.getBoundingClientRect());
    }
  };

  const onChipMouseOut = (e: MouseEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root || !hoveredChip) {
      return;
    }
    const leaving = findMentionChipFromTarget(root, e.target);
    if (!leaving || leaving !== hoveredChip) {
      return;
    }
    const related = e.relatedTarget;
    if (related instanceof Node && hoveredChip.contains(related)) {
      return;
    }
    setHoveredChip(null);
    setHoveredChipRect(null);
  };

  const clearHoveredChip = () => {
    setHoveredChip(null);
    setHoveredChipRect(null);
  };

  const hoveredMention = hoveredChip?.isConnected
    ? readMentionFromChipElement(hoveredChip)
    : null;
  const customPreview = hoveredMention
    ? (renderPreview?.(hoveredMention) ?? null)
    : null;
  const isInsightMention =
    !!hoveredMention && isInsightMentionType(hoveredMention.type);
  const labelEl = hoveredChip && getChipLabelElement(hoveredChip);
  const isLabelTruncated =
    !!labelEl && labelEl.scrollWidth > labelEl.clientWidth;
  const previewCard =
    customPreview ??
    (hoveredMention && (!isInsightMention || isLabelTruncated) ? (
      <span className={styles.chipLabelPreview}>{hoveredMention.label}</span>
    ) : null);

  // `inPortal` only relocates the tooltip bubble, not the anchor span below
  // (Tooltip's `children`), so the anchor's `position: fixed` still resolves
  // against its real ancestors — broken inside a Drawer, whose slide-in
  // `transform` redefines the containing block. This `createPortal` moves
  // the anchor itself out to `document.body` to keep it viewport-relative.
  const chipPreviewTooltip =
    previewCard && hoveredChipRect
      ? createPortal(
          <Tooltip
            label={previewCard}
            isOpen
            inPortal
            strategy="fixed"
            placement="top"
            hasDelay={false}
            dismissible={false}
            trigger={[]}
            hasMaxWidth={!customPreview}
          >
            <span
              aria-hidden
              className={styles.unresolvedTooltipAnchor}
              style={{
                top: hoveredChipRect.top,
                left: hoveredChipRect.left,
                width: hoveredChipRect.width,
                height: hoveredChipRect.height,
              }}
            />
          </Tooltip>,
          document.body
        )
      : null;

  return {
    onChipMouseOver,
    onChipMouseOut,
    clearHoveredChip,
    chipPreviewTooltip,
  };
};
